import { cva, type VariantProps } from 'class-variance-authority';
import { LoaderCircleIcon, type LucideIcon } from 'lucide-react';
import {
  createElement,
  isValidElement,
  type HTMLAttributes,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from 'react';

import { Skeleton } from '@/components/core/skeleton';
import { cn } from '@/shared/helpers';

const timelineMarkerVariants = cva(
  // Connectors are segmented and routed around each marker (see TimelineEntry), so a marker
  // sits in a gap between segments.
  'z-10 flex size-6 items-center justify-center rounded-full [&>svg]:size-3.5',
  {
    variants: {
      type: {
        success: 'bg-state-success text-state-success-foreground',
        progress: 'bg-primary text-primary-foreground',
        observe: 'bg-state-observe text-state-observe-foreground',
        alert: 'bg-state-alert text-state-alert-foreground',
        inactive: 'bg-state-inactive text-state-inactive-foreground',
      },
    },
    defaultVariants: {
      type: 'inactive',
    },
  },
);

export type TimelineType = NonNullable<VariantProps<typeof timelineMarkerVariants>['type']>;

/**
 * The default marker bead: a filled disc coloured by `type`.
 * Exported so callers that pass a custom `marker` to `TimelineEntry` can still reuse the
 * standard disc (e.g. a numbered step) without re-deriving its size/centering.
 */
export function TimelineMarker({
  type,
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement> & { type?: TimelineType }) {
  return (
    <span aria-hidden className={cn(timelineMarkerVariants({ type }), className)} {...props} />
  );
}

export function Timeline({ className, ...props }: HTMLAttributes<HTMLOListElement>) {
  // Each entry/section draws its own connector segment (see TimelineEntry /
  // TimelineSectionMarker); the list itself draws no line.
  return <ol className={cn('m-0 grid list-none gap-0 p-0 text-sm', className)} {...props} />;
}

export function TimelineSectionMarker({
  className,
  children,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLLIElement>>) {
  return (
    <li
      className={cn(
        'relative grid grid-cols-[1.5rem_minmax(0,1fr)] gap-x-3 pb-3',
        // The section dot is centred in its 1.5rem wrapper (dot spans 0.5rem–1rem), so the
        // connector is drawn as two segments to leave a symmetric 0.25rem gap on each side of
        // the dot regardless of what's above/below:
        //  · `before` = segment above the dot, reaching up to meet the previous item's line
        //    (negative top), suppressed on the first item so nothing dangles above it.
        '[&:not(:first-child)]:before:absolute [&:not(:first-child)]:before:top-[-0.25rem] [&:not(:first-child)]:before:left-3 [&:not(:first-child)]:before:h-2 [&:not(:first-child)]:before:w-0.5 [&:not(:first-child)]:before:-translate-x-1/2 [&:not(:first-child)]:before:bg-border',
        //  · `after` = segment below the dot, down to the next item, suppressed on the last item.
        '[&:not(:last-child)]:after:absolute [&:not(:last-child)]:after:top-5 [&:not(:last-child)]:after:bottom-1 [&:not(:last-child)]:after:left-3 [&:not(:last-child)]:after:w-0.5 [&:not(:last-child)]:after:-translate-x-1/2 [&:not(:last-child)]:after:bg-border',
        className,
      )}
      {...props}
    >
      <span className="z-10 flex size-6 items-center justify-center" aria-hidden>
        <span className="size-2 rounded-full bg-border" />
      </span>
      <span className="min-w-0 pt-0.5 font-heading text-xs leading-5 font-semibold tracking-wide text-muted-foreground uppercase">
        {children}
      </span>
    </li>
  );
}

export type TimelineEntryProps = PropsWithChildren<
  Omit<HTMLAttributes<HTMLLIElement>, 'type'> & {
    icon?: LucideIcon | ReactElement;
    type?: TimelineType;
    /**
     * Override the default disc marker entirely (e.g. an unfilled spinner ring or a numbered
     * bead); connectors are routed around it, so a transparent marker is fine. Takes
     * precedence over `icon`/`type`.
     */
    marker?: ReactNode;
  }
>;

export function TimelineEntry({
  icon: Icon,
  type,
  marker,
  className,
  children,
  ...props
}: TimelineEntryProps) {
  const defaultMarker = Icon ? (
    <TimelineMarker type={type}>
      {isValidElement(Icon) ? Icon : createElement(Icon as LucideIcon)}
    </TimelineMarker>
  ) : null;

  return (
    <li
      className={cn(
        'relative grid min-w-0 grid-cols-[1.5rem_minmax(0,1fr)] gap-x-3 pb-4 last:pb-0',
        // Connector segment: spans the gap from just below this marker to just above the next
        // one, clearing the marker so it can be transparent (e.g. a spinner ring).
        '[&:not(:last-child)]:before:absolute [&:not(:last-child)]:before:top-7 [&:not(:last-child)]:before:bottom-1 [&:not(:last-child)]:before:left-3 [&:not(:last-child)]:before:w-0.5 [&:not(:last-child)]:before:-translate-x-1/2 [&:not(:last-child)]:before:bg-border',
        className,
      )}
      {...props}
    >
      {marker ?? defaultMarker}
      <div className="min-w-0 pt-0.5">{children}</div>
    </li>
  );
}

export function TimelineSkeletonEntry({ className, ...props }: HTMLAttributes<HTMLLIElement>) {
  return (
    <TimelineEntry
      icon={<LoaderCircleIcon className="animate-spin" />}
      type="progress"
      className={className}
      {...props}
    >
      <TimelineEntryHeader>
        <TimelineEntryTitle>
          <Skeleton className="h-4 w-32" aria-hidden />
        </TimelineEntryTitle>
        <TimelineEntryMeta>
          <Skeleton className="h-3 w-20" aria-hidden />
        </TimelineEntryMeta>
      </TimelineEntryHeader>
      <TimelineEntryBody>
        <Skeleton className="h-3 w-full" aria-hidden />
      </TimelineEntryBody>
    </TimelineEntry>
  );
}

export function TimelineEntryHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1', className)}
      {...props}
    />
  );
}

export function TimelineEntryTitle({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('min-w-0 font-medium wrap-break-word text-foreground', className)}
      {...props}
    />
  );
}

export function TimelineEntryMeta({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('inline-flex min-w-0 items-center text-xs text-muted-foreground', className)}
      {...props}
    />
  );
}

export function TimelineEntryBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('mt-1 min-w-0 wrap-break-word text-muted-foreground', className)}
      {...props}
    />
  );
}
